import { UnroutedExchangeSummary } from '@chiroup/core/types/ConnectorAdmin.type';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import connectionApiService, { ConnectionApiHealth } from '../services/connectionApi.service';

const KEY_BASE = ['connection-api'] as const;
const VENDORS_KEY = [...KEY_BASE, 'vendors'] as const;
const HEALTH_KEY = [...KEY_BASE, 'health'] as const;
const TRACE_KEY = (vendor: string, integrationId: number) =>
  [...KEY_BASE, 'trace', vendor, integrationId] as const;

export const useConnectionApiHealth = () =>
  useQuery<ConnectionApiHealth>(HEALTH_KEY, () => connectionApiService.health(), {
    refetchOnWindowFocus: false,
    retry: false,
  });

export const useConnectorVendors = () =>
  useQuery(VENDORS_KEY, () => connectionApiService.vendors(), {
    refetchOnWindowFocus: false,
  });

export const useTraceConfig = (vendor?: string, integrationId?: number) =>
  useQuery(
    TRACE_KEY(vendor ?? '', integrationId ?? 0),
    () => connectionApiService.traceConfig(vendor!, integrationId!),
    { enabled: !!vendor && !!integrationId, refetchOnWindowFocus: false }
  );

/** All rotation/ops mutations invalidate the vendors list (its data changed). */
const useVendorsMutation = <TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) => {
  const queryClient = useQueryClient();

  return useMutation<TResult, unknown, TArgs>(fn, {
    onSuccess: () => queryClient.invalidateQueries(VENDORS_KEY),
  });
};

export const useRunCredentialCheck = () =>
  useVendorsMutation((vendor?: string) => connectionApiService.runCredentialCheck(vendor));

export const useStageCandidate = () =>
  useVendorsMutation(
    ({
      vendor,
      integrationId,
      apiKey,
    }: {
      vendor: string;
      integrationId: number;
      apiKey: string;
    }) => connectionApiService.stageCandidate(vendor, integrationId, apiKey)
  );

export const useValidateCandidate = () =>
  useVendorsMutation(({ vendor, integrationId }: { vendor: string; integrationId: number }) =>
    connectionApiService.validateCandidate(vendor, integrationId)
  );

export const usePromoteCandidate = () =>
  useVendorsMutation(({ vendor, integrationId }: { vendor: string; integrationId: number }) =>
    connectionApiService.promoteCandidate(vendor, integrationId)
  );

export const useRollbackCredentials = () =>
  useVendorsMutation(({ vendor, integrationId }: { vendor: string; integrationId: number }) =>
    connectionApiService.rollback(vendor, integrationId)
  );

const UNROUTED_KEY = [...KEY_BASE, 'unrouted'] as const;

export const useUnroutedExchanges = () =>
  useQuery(UNROUTED_KEY, () => connectionApiService.listUnrouted(), {
    refetchOnWindowFocus: false,
  });

/** Assign/close both remove the row — patch the cached list, don't refetch. */
const useUnroutedRemoval = <TArgs extends { exchangeId: string }, TResult>(
  fn: (args: TArgs) => Promise<TResult>
) => {
  const queryClient = useQueryClient();

  return useMutation<TResult, unknown, TArgs>(fn, {
    onSuccess: (_data, args) => {
      queryClient.setQueryData<{ rows: UnroutedExchangeSummary[] } | undefined>(
        UNROUTED_KEY,
        prev => (prev ? { rows: prev.rows.filter(r => r.exchangeId !== args.exchangeId) } : prev)
      );
    },
  });
};

export const useAssignUnrouted = () =>
  useUnroutedRemoval(({ exchangeId, clinicId }: { exchangeId: string; clinicId: number }) =>
    connectionApiService.assignUnrouted(exchangeId, clinicId)
  );

export const useCloseUnrouted = () =>
  useUnroutedRemoval(({ exchangeId, reason }: { exchangeId: string; reason: string }) =>
    connectionApiService.closeUnrouted(exchangeId, reason)
  );

export const useSetTraceConfig = (vendor?: string, integrationId?: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    (body: Parameters<typeof connectionApiService.setTraceConfig>[2]) =>
      connectionApiService.setTraceConfig(vendor!, integrationId!, body),
    {
      // The response IS the fresh config — patch the cache directly rather
      // than invalidate-and-refetch.
      onSuccess: data => {
        queryClient.setQueryData(TRACE_KEY(vendor ?? '', integrationId ?? 0), data);
      },
    }
  );
};
